import { Close, Delete, Edit } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import DebouncedTextField from 'components/DebouncedTextField';
import ConfirmationDialog from 'components/Dialogs/ConfirmationDialog';
import VirtualizedList from 'components/VirtualizedList/VirtualizedList';
import { useGetWindowHeight, useUserCanEdit } from 'hooks';
import ImportFinalizeReviewVirtualizedListItem from 'pages/CurationImport/components/ImportFinalizeReviewVirtualizedListItem';
import {
    useProjectCurationColumns,
    useProjectCurationImport,
    useProjectUser,
    useUpdateCurationImportName,
} from 'stores/projects/ProjectStore';
import { useEffect, useMemo, useRef, useState } from 'react';
import { EImportMode, ICurationStubStudy } from 'pages/Curation/Curation.types';
import { IGroupListItem } from './CurationBoardAIGroupsList';

const CurationBoardAIInterfaceImportSummary = ({
    group,
    onDeleteCurationImport,
}: {
    group: IGroupListItem;
    onDeleteCurationImport: (curationImportId: string) => void;
}) => {
    const projectUser = useProjectUser();
    const curationImport = useProjectCurationImport(group.id);
    const updateCurationImportName = useUpdateCurationImportName();
    const columns = useProjectCurationColumns();
    const [errorContainerHeight, setErrorContainerHeight] = useState<number>();
    const [descriptionContainerHeight, setDescriptionContainerHeight] = useState<number>();
    const errorTextContainerRef = useRef<HTMLDivElement>(null);
    const descriptionTextContainerRef = useRef<HTMLDivElement>(null);
    const windowHeight = useGetWindowHeight();
    const [confirmationDialogIsOpen, setConfirmationDialogIsOpen] = useState(false);
    const [editImportNameDialogIsOpen, setEditImportNameDialogIsOpen] = useState(false);
    const [importNameDraft, setImportNameDraft] = useState('');
    const [searchText, setSearchText] = useState<string>();
    const canEdit = useUserCanEdit(projectUser || undefined);

    useEffect(() => {
        const totalHeight =
            (errorTextContainerRef.current?.offsetHeight || 0) + (errorTextContainerRef.current ? 8 : 0); // add margin
        setErrorContainerHeight(totalHeight);
    }, [errorTextContainerRef.current?.offsetHeight, group.id]);

    useEffect(() => {
        setDescriptionContainerHeight(descriptionTextContainerRef.current?.offsetHeight || 0);
    }, [descriptionTextContainerRef.current?.offsetHeight, group.id]);

    useEffect(() => {
        if (editImportNameDialogIsOpen && curationImport?.name !== undefined) {
            setImportNameDraft(curationImport.name);
        }
    }, [editImportNameDialogIsOpen, curationImport?.name]);

    const studiesInImport = useMemo(() => {
        if (!curationImport?.id) return [];
        const allStudies = columns.reduce((acc, curr) => [...acc, ...curr.stubStudies], [] as ICurationStubStudy[]);
        return allStudies.filter((study) => study.importId === curationImport.id);
    }, [columns, curationImport]);

    const filteredStudies = useMemo(() => {
        if (!searchText) return studiesInImport;

        const searchTextLower = searchText.toLocaleLowerCase();

        return studiesInImport.filter(
            (study) =>
                study.title.toLocaleLowerCase()?.includes(searchTextLower) ||
                study.authors.toLocaleLowerCase()?.includes(searchTextLower) ||
                study.journal.toLocaleLowerCase()?.includes(searchTextLower)
        );
    }, [studiesInImport, searchText]);

    if (!curationImport) {
        return <Typography color="error.main">There was an error getting the import information</Typography>;
    }

    // Note: new Date('') yields "invalid date"
    const importDate = new Date(curationImport.date);
    let importMethodDescription = '';
    switch (curationImport.importModeUsed) {
        case EImportMode.FILE_IMPORT:
            importMethodDescription = 'These studies were imported from a file (RIS, endnote, or BibText).';
            break;
        case EImportMode.MANUAL_CREATE:
            importMethodDescription =
                'This study was created by manually entering the study details (name, description, authors, doi, etc).';
            break;
        case EImportMode.NEUROSTORE_IMPORT:
            importMethodDescription = "These studies were imported from neurostore's database.";
            break;
        case EImportMode.PUBMED_IMPORT:
            importMethodDescription = 'These studies were imported from a list of PubMed IDs.';
            break;
    }

    const listHeight = windowHeight - 356 - (errorContainerHeight || 0) - (descriptionContainerHeight || 0);

    return (
        <Box sx={{ padding: '1rem 2rem', overflowY: 'auto' }}>
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        gutterBottom={false}
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-all',
                        }}
                    >
                        {curationImport?.name || ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Dialog
                            open={editImportNameDialogIsOpen}
                            onClose={() => setEditImportNameDialogIsOpen(false)}
                            fullWidth
                            maxWidth="sm"
                        >
                            <DialogTitle>Edit import name</DialogTitle>
                            <DialogContent>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    label="Import name"
                                    fullWidth
                                    value={importNameDraft}
                                    onChange={(e) => setImportNameDraft(e.target.value)}
                                    data-testid="curation-import-name-input"
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    data-testid="curation-import-name-cancel"
                                    onClick={() => setEditImportNameDialogIsOpen(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    data-testid="curation-import-name-save"
                                    variant="contained"
                                    disabled={!importNameDraft.trim()}
                                    disableElevation
                                    onClick={() => {
                                        const trimmed = importNameDraft.trim();
                                        if (!trimmed) return;
                                        updateCurationImportName(curationImport.id, trimmed);
                                        setEditImportNameDialogIsOpen(false);
                                    }}
                                >
                                    Save
                                </Button>
                            </DialogActions>
                        </Dialog>
                        <ConfirmationDialog
                            dialogTitle="Are you sure you want to delete this import?"
                            onCloseDialog={(ok) => {
                                if (ok) onDeleteCurationImport(curationImport.id);
                                setConfirmationDialogIsOpen(false);
                            }}
                            rejectText="Cancel"
                            confirmText="Delete"
                            isOpen={confirmationDialogIsOpen}
                            rejectButtonProps={{
                                color: 'primary',
                            }}
                            confirmButtonProps={{
                                color: 'error',
                            }}
                        />
                        <Button
                            variant="contained"
                            sx={{ minWidth: '100px', fontSize: '12px' }}
                            endIcon={<Edit style={{ fontSize: '16px' }} />}
                            disabled={!canEdit}
                            disableElevation
                            size="small"
                            data-testid="curation-edit-import-button"
                            onClick={() => setEditImportNameDialogIsOpen(true)}
                        >
                            Edit
                        </Button>
                        <Button
                            color="error"
                            sx={{ fontSize: '12px' }}
                            endIcon={<Delete style={{ fontSize: '16px' }} />}
                            variant="contained"
                            disabled={!canEdit}
                            disableElevation
                            size="small"
                            onClick={() => setConfirmationDialogIsOpen(true)}
                        >
                            Delete import
                        </Button>
                    </Box>
                </Box>
                <Box ref={descriptionTextContainerRef}>
                    <Typography color="muted.main" variant="caption" sx={{ lineHeight: 'normal', fontWeight: 'bold' }}>
                        {importMethodDescription}
                    </Typography>
                    {curationImport.neurostoreSearchParams && (
                        <Typography
                            variant="caption"
                            color="muted.main"
                            sx={{ wordBreak: 'break-all', display: 'block' }}
                        >
                            Search: {curationImport.neurostoreSearchParams}
                        </Typography>
                    )}
                </Box>
                <Chip
                    sx={{ fontSize: '14px', height: '24px', marginTop: '4px' }}
                    label={`${importDate.toDateString()} ${importDate.toLocaleTimeString()}`}
                />
                {curationImport.errorsDuringImport && (
                    <Alert
                        ref={errorTextContainerRef}
                        severity="error"
                        variant="standard"
                        sx={{
                            marginTop: '8px',
                            padding: '0px 4px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            '.MuiAlert-icon': { fontSize: '18px' },
                        }}
                    >
                        {curationImport.errorsDuringImport}
                    </Alert>
                )}
            </Box>
            <Box sx={{ backgroundColor: '#fafafa', padding: '0.5rem', borderRadius: '4px', marginTop: '8px' }}>
                <Box>
                    <DebouncedTextField
                        placeholder="Search by title, authors, or journal"
                        size="small"
                        onChange={setSearchText}
                        value={searchText}
                        InputProps={{
                            endAdornment: (
                                <IconButton size="small" onClick={() => setSearchText(undefined)}>
                                    <Close />
                                </IconButton>
                            ),
                        }}
                        sx={{
                            backgroundColor: 'white',
                            width: '100%',
                            marginBottom: '8px',
                            input: { padding: '7px 12px', fontSize: '14px' },
                        }}
                    />
                </Box>
                <VirtualizedList
                    rows={filteredStudies}
                    listHeightInPx={listHeight < 0 ? 0 : listHeight}
                    overscan={3}
                    getItemKey={(stub) => stub.id}
                    renderRow={(stub, style) => <ImportFinalizeReviewVirtualizedListItem {...stub} style={style} />}
                />
            </Box>
        </Box>
    );
};

export default CurationBoardAIInterfaceImportSummary;
